/**
 * PDF Generation Types
 *
 * These types support a template-driven PDF generation system using pdfmake.
 * Templates can be extended to support different invoice layouts in the future.
 */

import type { TDocumentDefinitions, Content, StyleDictionary, PageSize, Margins, ContentColumns } from 'pdfmake/interfaces';

/** Page size options */
export type PdfPageSize = PageSize;

/** Margin options - can be a single number, [h, v], or [l, t, r, b] */
export type PdfMargins = Margins;

/**
 * Document-level options for PDF generation
 */
export interface PdfDocumentOptions {
    /** Page size (default: 'LETTER') */
    pageSize?: PdfPageSize;
    /** Page margins (default: 36 = 0.5in) */
    pageMargins?: PdfMargins;
    /** Document title for metadata */
    title?: string;
    /** Document subject for metadata */
    subject?: string;
    /** Document author for metadata */
    author?: string;
    /** Keywords for metadata */
    keywords?: string;
    /** Default font family */
    defaultFont?: string;
    /** Default font size */
    defaultFontSize?: number;
}

/**
 * Table column definition
 */
export interface PdfTableColumn {
    /** Column header text */
    header: string;
    /** Data field name or accessor function */
    field: string | ((row: Record<string, unknown>) => string | number);
    /** Column width - can be 'auto', '*', or a fixed number */
    width?: 'auto' | '*' | number;
    /** Text alignment */
    alignment?: 'left' | 'center' | 'right';
    /** Whether to apply currency formatting */
    isCurrency?: boolean;
    /** Whether to apply date formatting */
    isDate?: boolean;
    /** Whether this is a text column that may need wrapping */
    wrap?: boolean;
    /** Prevent text wrapping in this column */
    noWrap?: boolean;
    /** Whether the value contains HTML that should be rendered as rich content */
    isHtml?: boolean;
    /** Optional style name to apply */
    style?: string;
}

/**
 * Options for table generation
 */
export interface PdfTableOptions {
    /** Column definitions */
    columns: PdfTableColumn[];
    /** Data rows */
    data: Record<string, unknown>[];
    /** Whether to show header row */
    showHeader?: boolean;
    /** Whether to use alternating row colors */
    alternatingRows?: boolean;
    /** Color for alternating rows */
    alternatingColor?: string;
    /** Whether to include a totals row */
    showTotals?: boolean;
    /** Footer row data (e.g., totals) */
    footer?: Record<string, unknown>;
    /** Header background color */
    headerBackground?: string;
    /** Header text color */
    headerColor?: string;
    /** Border color */
    borderColor?: string;
    /** Table width - default fills available space */
    tableWidth?: number | 'auto';
    /** Custom styles to apply */
    customStyles?: StyleDictionary;
}

/**
 * Row in an account summary
 */
export interface AccountSummaryRow {
    label: string;
    amount: number;
    /** 'total' = bold with green background, 'header' = bold no background, 'subtotal' = indented, 'amount-due' = bold yellow */
    type: 'total' | 'header' | 'subtotal' | 'amount-due';
    /** Optional sub-label for date or description */
    subLabel?: string;
}

/**
 * Data for account summary component
 */
export interface AccountSummaryData {
    rows: AccountSummaryRow[];
    /** Background color for total rows (default: #e8f5e9) */
    totalBackground?: string;
    /** Background color for amount due row (default: #fff9c4) */
    amountDueBackground?: string;
}

/**
 * Transaction in trust accounting ledger
 */
export interface TrustTransaction {
    date: Date | string;
    type: string;
    description?: string;
    amount: number;
    isNegative: boolean;
    runningBalance: number;
}

/**
 * A single trust account (case or client)
 */
export interface TrustAccount {
    /** Account name (case name or client name) */
    name: string;
    /** Whether this is a case trust vs client trust */
    type: 'case' | 'client';
    /** Starting balance */
    startingBalance: number;
    /** Transactions in this period */
    transactions: TrustTransaction[];
    /** Ending balance */
    endingBalance: number;
}

/**
 * Data for trust accounting section
 */
export interface TrustAccountingData {
    /** Case trust account */
    caseTrust?: TrustAccount;
    /** Client trust accounts */
    clientTrusts: TrustAccount[];
}

/**
 * Letterhead/header data
 */
export interface LetterheadData {
    /** HTML content from firm letterhead setting */
    html?: string;
    /** Fallback: firm name */
    firmName?: string;
    /** Optional tagline */
    tagline?: string;
    /** Firm address line */
    address?: string;
}

/**
 * Client information block data
 */
export interface ClientInfoData {
    /** Client billing name(s) */
    clientName: string;
    /** Client address lines */
    address?: string[];
    /** Invoice number */
    invoiceNumber: string;
    /** Invoice date */
    invoiceDate: Date | string;
    /** Due date */
    dueDate?: Date | string;
    /** Matter/case name */
    matterName?: string;
    /** Issue period start */
    periodStart?: Date | string;
    /** Issue period end */
    periodEnd?: Date | string;
}

/**
 * QR code data for payment portal
 */
export interface QrCodeData {
    /** URL to encode */
    url: string;
    /** Label text below QR code */
    label?: string;
    /** Size in points (default: 80) */
    size?: number;
}

/**
 * Task/labor line item for invoice
 */
export interface TaskLineItem {
    date: Date | string;
    user?: string;
    activity?: string;
    description: string;
    time: number;
    rate: number;
    cost: number;
    /** For narrative mode - group key */
    groupKey?: string;
}

/**
 * Expense line item
 */
export interface ExpenseLineItem {
    date: Date | string;
    payee?: string;
    description: string;
    quantity: number;
    cost: number;
    total: number;
}

/**
 * Flat fee line item
 */
export interface FlatFeeLineItem {
    description: string;
    amount: number;
    earned: boolean;
}

/**
 * Template-specific data and configuration
 * This interface can be extended by specific template implementations
 */
export interface InvoiceTemplateData {
    /** Template identifier (for future multi-template support) */
    templateId?: string;

    /** Invoice mode: Narrative (N) or Detailed (D) */
    mode: 'N' | 'D';

    /** Is this a court invoice (simplified format) */
    courtInvoice?: boolean;

    /** Letterhead information */
    letterhead: LetterheadData;

    /** Client information */
    clientInfo: ClientInfoData;

    /** Account summary data */
    accountSummary: AccountSummaryData;

    /** Labor/task items */
    tasks: TaskLineItem[];

    /** Expense items */
    expenses: ExpenseLineItem[];

    /** Flat fee items */
    flatFees: FlatFeeLineItem[];

    /** Trust accounting data (null for leads) */
    trustAccounting?: TrustAccountingData;

    /** QR code for payment portal */
    qrCode?: QrCodeData;

    /** Invoice description/notes */
    description?: string;

    /** Custom invoice narrative header (for narrative mode) */
    narrativeHeader?: string;

    /** Whether to show trust accounting section */
    showTrustAccounting: boolean;
}

/**
 * Result of PDF generation
 */
export interface PdfGenerationResult {
    /** Base64-encoded PDF data URL */
    dataUrl: string;
    /** Generated filename */
    filename: string;
    /** PDF blob for direct download, and the file Save to Documents attaches */
    blob: Blob;
}

/**
 * A template renderer function that produces pdfmake content
 */
export type TemplateRenderer<T = InvoiceTemplateData> = (data: T) => Content[];

/**
 * A complete document template including styles and structure
 */
export interface PdfTemplate<T = InvoiceTemplateData> {
    /** Template identifier */
    id: string;
    /** Human-readable name */
    name: string;
    /** Default document options */
    defaultOptions: PdfDocumentOptions;
    /** Style definitions */
    styles: StyleDictionary;
    /** Render function that produces document content */
    render: TemplateRenderer<T>;
}

/**
 * Color definitions for consistent styling
 */
export const PDF_COLORS = {
    /** Green background for totals */
    totalGreen: '#e8f5e9',
    /** Yellow background for amount due */
    amountDueYellow: '#fff9c4',
    /** Green text for positive amounts */
    positiveGreen: '#2e7d32',
    /** Red text for negative amounts */
    negativeRed: '#c62828',
    /** Header background */
    headerGray: '#f5f5f5',
    /** Border color */
    borderGray: '#e0e0e0',
    /** Text color */
    textBlack: '#212121',
    /** Muted text */
    textMuted: '#757575',
} as const;

/**
 * Type guard to check if content is a columns array
 */
export function isContentColumns(content: Content): content is ContentColumns {
    return typeof content === 'object' && content !== null && 'columns' in content;
}
